"use client";

import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@workspace/ui/components/alert-dialog";
import { memo, useCallback, useState } from "react";
import { Spinner } from "@workspace/ui/components/spinner";
import { toast } from "@workspace/ui/hooks/use-sonner-toast";
import { useSupabase } from "@/components/providers/supabase-auth-provider";

/**
 * Modal shown when a user signs in while their account is pending deletion.
 * Gives them the choice to cancel the deletion (restore) or sign out.
 * Non-dismissible — user MUST choose an action.
 */
export const DeletionRecoveryModal = memo(() => {
	const { deletionRecovery, handleDeletionRecovery } = useSupabase();
	const [isProcessing, setIsProcessing] = useState(false);

	const scheduledDate = deletionRecovery?.scheduledDate
		? new Date(deletionRecovery.scheduledDate).toLocaleDateString("en-US", {
				month: "long",
				day: "numeric",
				year: "numeric",
			})
		: null;

	const handleRestore = useCallback(async () => {
		setIsProcessing(true);
		try {
			await handleDeletionRecovery(true);
		} catch {
			toast({
				variant: "destructive",
				title: "Failed to restore account",
				description: "Please try again or sign out and contact support.",
			});
		} finally {
			setIsProcessing(false);
		}
	}, [handleDeletionRecovery]);

	const handleSignOut = useCallback(async () => {
		setIsProcessing(true);
		await handleDeletionRecovery(false);
		setIsProcessing(false);
	}, [handleDeletionRecovery]);

	if (!deletionRecovery) return null;

	return (
		<AlertDialog open>
			<AlertDialogContent>
				<AlertDialogHeader>
					<AlertDialogTitle>Account Pending Deletion</AlertDialogTitle>
					<AlertDialogDescription asChild>
						<div className="space-y-3">
							<p>
								Your account is scheduled for permanent deletion on{" "}
								<strong className="text-foreground">{scheduledDate}</strong>.
							</p>
							<p>
								Continuing will cancel the deletion request and restore your
								account. If you want to delete your account later, you&apos;ll
								need to request it again from Settings.
							</p>
						</div>
					</AlertDialogDescription>
				</AlertDialogHeader>
				<AlertDialogFooter>
					<AlertDialogCancel onClick={handleSignOut} disabled={isProcessing}>
						{isProcessing ? (
							<div className="flex items-center gap-2">
								<Spinner size="sm" variant="current" />
								<span>Signing out...</span>
							</div>
						) : (
							"Sign Out"
						)}
					</AlertDialogCancel>
					<AlertDialogAction onClick={handleRestore} disabled={isProcessing}>
						{isProcessing ? (
							<div className="flex items-center gap-2">
								<Spinner size="sm" variant="current" />
								<span>Restoring...</span>
							</div>
						) : (
							"Cancel Deletion & Restore"
						)}
					</AlertDialogAction>
				</AlertDialogFooter>
			</AlertDialogContent>
		</AlertDialog>
	);
});

DeletionRecoveryModal.displayName = "DeletionRecoveryModal";
